import { computed, type ComputedRef } from 'vue'
import { UNLIMITED_CAPACITY } from '~/components/PurchaseContract/Program/course-capacity'
import type {
    BookingStatusAvailableResult,
    CourseBookingStatus
} from '~/resources/graphql/graphql'
import { useViewer } from '#imports'
import { useI18n } from 'vue-i18n'
import {
    useBookingPeriodMessage,
    type BookingPeriodData
} from './use-booking-period'

export function useCourseAvailabilityMessage(
    waitlistEnabled: ComputedRef<boolean>,
    availabilityThreshold: ComputedRef<number | null>,
    courseData: ComputedRef<{
        bookingStatus: CourseBookingStatus
        bookingStatusAvailable: BookingStatusAvailableResult[]
        bookingPeriod: BookingPeriodData
        capacityResult: {
            isWaitlistFull: boolean
            overlappedBookedSessions: number
            contracts: number
            enrollments: number
        }
    }>
) {
    const viewer = useViewer()
    const { t } = useI18n({ useScope: 'global' })
    const { openingMessage } = useBookingPeriodMessage()

    return computed<{
        message: string
        theme: 'danger' | 'success' | 'base'
        isOpeningMessage?: boolean
    }>(() => {
        // Booking status closed
        if (courseData.value.bookingStatus === 'CLOSED') {
            // No joining waitlist when restricted in this way
            return {
                message: 'Booking is closed',
                theme: 'danger'
            }
        }

        // If the asset schedule isn't allowing more sessions to be booked, you can't book
        if (courseData.value.capacityResult.overlappedBookedSessions <= 0) {
            // No joining waitlist when restricted in this way
            return {
                message: 'Course is full',
                theme: 'danger'
            }
        }

        // Waitlist joining has its own cutoff; the public period applies to completing a booking.
        // If no more contracts are allowed to be booked
        if (courseData.value.capacityResult.contracts <= 0) {
            if (waitlistEnabled.value) {
                if (courseData.value.capacityResult.isWaitlistFull) {
                    return {
                        message: 'Course and waitlist full',
                        theme: 'danger'
                    }
                } else {
                    return {
                        message: 'Course is full. Join the waitlist below',
                        theme: 'danger'
                    }
                }
            }

            return {
                message: 'Course is full',
                theme: 'danger'
            }
        }

        // If available enrollments is 0, return full
        // There is an exception if the customer is on the waitlist
        const enrollmentCapacity = courseData.value.capacityResult.enrollments
        const canOverrideEnrollmentCapacity =
            waitlistEnabled.value &&
            courseData.value.bookingStatus === 'WAITLIST' &&
            courseData.value.bookingStatusAvailable.some(
                status => status.overrideCapacityConstraints
            )
        if (enrollmentCapacity <= 0 && !canOverrideEnrollmentCapacity) {
            if (waitlistEnabled.value) {
                if (courseData.value.capacityResult.isWaitlistFull) {
                    return {
                        message: 'Course and waitlist full',
                        theme: 'danger'
                    }
                } else {
                    return {
                        message: 'Course is full. Join the waitlist below',
                        theme: 'danger'
                    }
                }
            }

            return {
                message: 'Course is full',
                theme: 'danger'
            }
        }

        // If booking status is waitlist, restricted booking
        if (courseData.value.bookingStatus === 'WAITLIST') {
            if (viewer.value) {
                if (courseData.value.bookingStatusAvailable.length === 0) {
                    let message = 'Waitlist booking only'

                    if (waitlistEnabled.value) {
                        if (courseData.value.capacityResult.isWaitlistFull) {
                            message += '. Waitlist is full'
                        } else {
                            message += '. Join the waitlist below'
                        }
                    }

                    return {
                        message,
                        theme: 'danger'
                    }
                }
            } else {
                return {
                    message: 'Waitlist only. Sign in required',
                    theme: 'danger'
                }
            }
        }

        // If our booking period is not available, return booking period message
        if (courseData.value.bookingPeriod.status === 'AFTER_BOOKING_PERIOD') {
            return {
                message: t('purchaseContract.bookingClosed'),
                theme: 'danger'
            }
        }

        if (courseData.value.bookingPeriod.status === 'BEFORE_BOOKING_PERIOD') {
            return {
                message: openingMessage(courseData.value.bookingPeriod),
                theme: 'base',
                isOpeningMessage: true
            }
        }

        // Waitlist spot is ready
        // Earlier checks require a signed-in customer with an offer and an open booking period.
        if (courseData.value.bookingStatus === 'WAITLIST') {
            return {
                message: 'Your spot is available',
                theme: 'success'
            }
        }

        // --Course Available--
        // If we have more capacity than the availability display threshold, or there is unlimited capacity
        if (
            (availabilityThreshold.value !== null &&
                enrollmentCapacity > availabilityThreshold.value) ||
            enrollmentCapacity === UNLIMITED_CAPACITY
        ) {
            return {
                message: 'Available',
                theme: 'base'
            }
        }

        // Otherwise list spaces available
        if (enrollmentCapacity > 1) {
            return {
                message: enrollmentCapacity + ' spaces available',
                theme: 'base'
            }
        } else if (enrollmentCapacity === 1) {
            return {
                message: '1 space available',
                theme: 'base'
            }
        }

        throw new Error()
    })
}
